import { LocalDate } from "@js-joda/core";
import { Suspense, useCallback, useEffect, useMemo } from "react";
import { Alert } from "react-bootstrap";
import { Link, useNavigate } from "react-router";
import { useParams } from "react-router-dom";
import { canApprove, PostStatus } from "shared";
import { useBillingPlanPaidCheck } from "../billing/useSubscription";
import { IconSVG } from "../common/Icon";
import { useModal } from "../common/modal/Modal";
import { SimpleTooltip } from "../common/SimpleTooltip";
import { useCompany } from "../company/useCompany";
import {
	ButtonDropdownMenu,
	type DropdownActionProps,
} from "../components/ButtonDropdownMenu";
import { ButtonIcon } from "../components/ButtonIcon";
import { ModalGuidelines } from "../guidelines/ModalGuidelines";
import { useIsMobile } from "../hooks/useBreakpoint";
import { LinkGen } from "../LinkGen";
import { AppPage } from "../layout/AppLayout";
import { ModalBulkApprove } from "../post/ModalBulkApprove";
import { ModalDeleteScheduledPosts } from "../post/ModalDeleteScheduledPosts";
import { ModalGeneratePosts } from "../post/ModalGeneratePosts";
import { useIntegrations } from "../settings/useIntegrations";
import {
	CalendarProvider,
	type CalendarType,
	type CalendarViewType,
	useCalendar,
} from "./CalendarContext";
import { CalendarGrid } from "./CalendarGrid";
import { CalendarList } from "./CalendarList";
import { CalendarSkeleton } from "./CalendarSkeleton";
import { CalendarView } from "./CalendarView";
import { useSuspensePosts, useSuspensePostsListRange } from "./usePosts";

export const PageCalendar = () => {
	const { open } = useModal();
	const { date: url_date, type, view } = useParams();
	const navigate = useNavigate();
	const isMobile = useIsMobile();
	const { atLeastOneConnected } = useIntegrations();

	const date = useMemo(() => {
		try {
			return LocalDate.parse(url_date ?? "");
		} catch {
			return LocalDate.now();
		}
	}, [url_date]);

	// Prefer list view on mobile — calendar grid is hard to use on narrow screens
	useEffect(() => {
		if (isMobile && view === "calendar") {
			navigate(LinkGen.Calendar(date, type ?? "month", "list"), {
				replace: true,
			});
		}
	}, [isMobile, view, date, type, navigate]);

	const handleOpenGenerateModal = useCallback(
		(isBlog = false) => open(ModalGeneratePosts, { isBlog }),
		[open],
	);

	const handleOpenDeletePost = useCallback(
		() => open(ModalDeleteScheduledPosts, {}),
		[open],
	);

	const handleOpenGuidelines = useCallback(
		() => open(ModalGuidelines, {}),
		[open],
	);

	const createDropdownItems: Array<DropdownActionProps> = useMemo(
		() => [
			{
				label: "Social media post",
				props: { onClick: () => navigate(LinkGen.PostCreate()) },
			},
			{
				label: "Blog post",
				props: { onClick: () => navigate(LinkGen.BlogPostCreate()) },
			},
		],
		[navigate],
	);
	const generateDropdownItems: Array<DropdownActionProps> = useMemo(() => {
		const items: Array<DropdownActionProps> = [
			{
				label: "Social Media posts",
				props: { onClick: () => handleOpenGenerateModal() },
			},
			{
				label: "Blog posts",
				props: { onClick: () => handleOpenGenerateModal(true) },
			},
		];

		return items;
	}, [handleOpenGenerateModal]);

	const isPlanPaid = useBillingPlanPaidCheck();

	return (
		<AppPage
			title="Calendar"
			headerActions={
				<>
					<SimpleTooltip tooltip="Post Guidelines" placement="top">
						<ButtonIcon onClick={handleOpenGuidelines} icon="rule" />
					</SimpleTooltip>
					<SimpleTooltip tooltip="Delete scheduled posts" placement="top">
						<ButtonIcon onClick={handleOpenDeletePost} icon="delete" />
					</SimpleTooltip>
					<ButtonDropdownMenu
						main={{
							label: "Create...",
							props: {
								icon: "add_circle",
							},
						}}
						options={createDropdownItems}
					/>
					<ButtonDropdownMenu
						main={{
							label: "Generate...",
							props: {
								icon: IconSVG.Sparkle,
							},
						}}
						options={generateDropdownItems}
					/>
				</>
			}
		>
			<CalendarProvider
				date={date}
				onDateChange={(value) => navigate(LinkGen.Calendar(value, type, view))}
				type={type as CalendarType}
				view={view as CalendarViewType}
			>
				{!atLeastOneConnected ? (
					<Alert variant="warning">
						No social networks are currently connected to your profile. Please
						visit our{" "}
						<Link to={LinkGen.Settings.Apps()}>
							Apps section under Settings
						</Link>{" "}
						to connect your social networks.
					</Alert>
				) : null}
				{atLeastOneConnected && !isPlanPaid && (
					<Alert variant="warning">
						Posts are not published if you don't have a plan. Please view the{" "}
						<Link to={LinkGen.Settings.Billing()}>Billing Settings here</Link>!
					</Alert>
				)}
				<CalendarContent />
			</CalendarProvider>
		</AppPage>
	);
};

const CalendarApproveButton = () => {
	const { open } = useModal();
	const { days } = useCalendar();
	const company = useCompany();
	const { IDs } = useSuspensePostsListRange(days[0], days[days.length - 1]);
	const posts = useSuspensePosts(IDs);

	const canApprovePost = company.myRole ? canApprove(company.myRole) : false;
	const approvableCount = posts.filter(
		(p) =>
			p.status === PostStatus.PENDING_APPROVAL ||
			(company.requirePostApproval && p.status === PostStatus.DRAFT),
	).length;

	const handleOpenBulkApprove = useCallback(
		() => open(ModalBulkApprove, { posts }),
		[open, posts],
	);

	if (!canApprovePost || approvableCount === 0) {
		return null;
	}

	return (
		<SimpleTooltip
			tooltip={`Approve posts (${approvableCount} awaiting approval)`}
			placement="top"
		>
			<ButtonIcon
				onClick={handleOpenBulkApprove}
				icon="check_circle"
				className="calendar-approve-btn"
			/>
		</SimpleTooltip>
	);
};

const CalendarBody = () => {
	const { view } = useCalendar();
	return (
		<>
			<div className="calendar-header-extra mb-3">
				<CalendarApproveButton />
			</div>
			{view === "calendar" ? <CalendarGrid /> : <CalendarList />}
		</>
	);
};

const CalendarContent = () => {
	const { view, type } = useCalendar();
	return (
		<>
			<CalendarView />
			<Suspense fallback={<CalendarSkeleton view={view} type={type} />}>
				<CalendarBody />
			</Suspense>
		</>
	);
};
